import { useState, useEffect } from "react";
import * as L from "leaflet";
import * as turf from "@turf/turf";
import lineIntersect from "@turf/line-intersect";
import { LeafletEventHandlerFnMap, LatLng } from "leaflet";
import { Marker, Polyline, Polygon, useMap } from "react-leaflet";
import { useAsset } from "../useAsset";
import { Annotation } from "../../../model/internal/annotation";
import { BLUE_HIGHLIGHT, DEFAULT_ANNOTATION_COLOR } from "../../../constants/colors";
import { ShapeLineWidth } from "../../../constants/annotation";
import { useDispatch } from "react-redux";
import { ZIndex } from "../../../constants/zindex";
import { updateShapeAnnotationDirectly } from "../../../app/networkSlice";
import { enforceTrueRectangle } from "../../../utils/shape/enforceTrueRectangle";
import { circleGrabHandleIcon } from "../../../utils/shape/transform";

const rotateHandleLength = 64;

interface RectangleAnnotationProps {
  rectangle: Annotation;
  draggable?: boolean;
  isDragging: boolean;
  highlighted: boolean;
  easyClickTolerance: number;
  arrowSnappingActive: boolean;
  eventHandlers?: LeafletEventHandlerFnMap;
}

const RectangleAnnotation = ({
  rectangle,
  draggable,
  isDragging,
  highlighted,
  easyClickTolerance,
  arrowSnappingActive,
  eventHandlers,
}: RectangleAnnotationProps) => {
  const { isAssetSelected, isAssetRingfenced } = useAsset();
  const [draggingVertexIndex, setDraggingVertexIndex] = useState<number>(-1);
  const [isRotating, setIsRotating] = useState<boolean>(false);
  const [draggingGeometry, setDraggingGeometry] = useState<LatLng[]>([]);
  const [draggingMarkerPosition, setDraggingMarkerPosition] = useState<LatLng | null>(null);
  const dispatch = useDispatch();
  const map = useMap();

  const isSelected = isAssetSelected(rectangle.id);
  const isRingfenced = isAssetRingfenced(rectangle.id);

  // Dragend is not always fired, eg when user presses escape while dragging
  // Hence clear dragging state using mouseup as fallback
  useEffect(() => {
    const handleMouseUp = () => {
      if (draggingGeometry.length > 0) {
        setDraggingVertexIndex(-1);
        setIsRotating(false);
        setDraggingGeometry([]);
      }
    };

    if (draggingGeometry.length > 0) {
      window.addEventListener("mouseup", handleMouseUp);
      return () => window.removeEventListener("mouseup", handleMouseUp);
    }
  }, [draggingGeometry]);

  const getCenterOfLine = (coords: L.Point[]) => {
    return new L.Point((coords[0].x + coords[1].x) / 2, (coords[0].y + coords[1].y) / 2);
  };

  const getRotateHandlePositions = () => {
    const rectangleVertices = rectangle.geometry.map((g: any) => map.latLngToContainerPoint(g));
    const centerOfRectangle = getCenterOfLine([rectangleVertices[0], rectangleVertices[2]]);
    const verticalPoint = new L.Point(
      centerOfRectangle.x,
      centerOfRectangle.y - rotateHandleLength,
    );
    if (!!draggingMarkerPosition) {
      return [map.containerPointToLatLng(centerOfRectangle), draggingMarkerPosition];
    }
    return [
      map.containerPointToLatLng(centerOfRectangle),
      map.containerPointToLatLng(verticalPoint),
    ];
  };

  const getExtendedLine = (latlngCenter: LatLng, bearing: number, meters: number) => {
    return turf.lineString([
      turf.destination([latlngCenter.lng, latlngCenter.lat], -meters, bearing, { units: "meters" })
        .geometry.coordinates,
      turf.destination([latlngCenter.lng, latlngCenter.lat], meters, bearing, { units: "meters" })
        .geometry.coordinates,
    ]);
  };

  const getRectangleGeometryDraggedByVertex = (index: number, latlng: LatLng) => {
    // Vertices are named assuming clockwise order, and user is dragging TopRight, but it works for any scenario
    // TR point is being moved
    // BL point is anchored in position, as it is opposite the point being moved
    // Draw lines from the BL and TR points, and get the intersection to determine the new TL and BR points

    const newGeometry = [...rectangle.geometry];

    try {
      const indexTR = index;
      const indexBR = (indexTR + 1) % 4;
      const indexBL = (indexBR + 1) % 4;
      const indexTL = (indexBL + 1) % 4;

      const pointBL = turf.point([
        rectangle.geometry[indexBL].lng,
        rectangle.geometry[indexBL].lat,
      ]);
      const pointTL = turf.point([
        rectangle.geometry[indexTL].lng,
        rectangle.geometry[indexTL].lat,
      ]);
      const pointBR = turf.point([
        rectangle.geometry[indexBR].lng,
        rectangle.geometry[indexBR].lat,
      ]);
      const bearingBLtoTL = turf.bearing(pointBL, pointTL);
      const bearingBLtoBR = turf.bearing(pointBL, pointBR);

      const latlngBL = rectangle.geometry[indexBL];
      const latlngTR = latlng;

      const newDiagonalLine = turf.lineString([
        [rectangle.geometry[indexBL].lng, rectangle.geometry[indexBL].lat],
        [latlng.lng, latlng.lat],
      ]);
      const lengthUpperBound = turf.length(newDiagonalLine, { units: "meters" }) * 2;

      const lineBLtoTLextended = getExtendedLine(latlngBL, bearingBLtoTL, lengthUpperBound);
      const lineBLtoBRextended = getExtendedLine(latlngBL, bearingBLtoBR, lengthUpperBound);
      const lineTRtoBRextended = getExtendedLine(latlngTR, bearingBLtoTL, lengthUpperBound);
      const lineTRtoTLextended = getExtendedLine(latlngTR, bearingBLtoBR, lengthUpperBound);

      const newTL = lineIntersect(lineBLtoTLextended, lineTRtoTLextended)?.features[0]?.geometry
        ?.coordinates;
      const newBR = lineIntersect(lineBLtoBRextended, lineTRtoBRextended)?.features[0]?.geometry
        ?.coordinates;

      if (!!newTL && !!newBR) {
        // BL point has not changed
        newGeometry[indexTR] = latlng;
        newGeometry[indexTL] = new LatLng(newTL[1], newTL[0]);
        newGeometry[indexBR] = new LatLng(newBR[1], newBR[0]);
      }
    } catch (e) {}
    return newGeometry;
  };

  const getRectangleGeometryRotated = (latlng: LatLng) => {
    const rectangleVertices = rectangle.geometry.map((g: any) => map.latLngToContainerPoint(g));
    const centerOfRectangle = getCenterOfLine([rectangleVertices[0], rectangleVertices[2]]);
    const dragPosition = map.latLngToContainerPoint(latlng);

    const rotationAngle =
      Math.atan2(centerOfRectangle.y - dragPosition.y, dragPosition.x - centerOfRectangle.x) -
      Math.PI / 2;

    const newVertices = rectangleVertices.map((vertex: L.Point) => {
      const oldAngle = Math.atan2(centerOfRectangle.y - vertex.y, vertex.x - centerOfRectangle.x);
      const length = Math.sqrt(
        Math.pow(vertex.x - centerOfRectangle.x, 2) + Math.pow(centerOfRectangle.y - vertex.y, 2),
      );
      const newAngle = oldAngle + rotationAngle;
      return new L.Point(
        centerOfRectangle.x + length * Math.cos(newAngle),
        centerOfRectangle.y - length * Math.sin(newAngle),
      );
    });

    const newGeometry = newVertices.map((vertex: L.Point) => map.containerPointToLatLng(vertex));
    return newGeometry;
  };

  const handleTransformEnd = (newGeometry: LatLng[]) => {
    const modifiedGeometry = enforceTrueRectangle(map, newGeometry as L.LatLng[]);
    dispatch(
      updateShapeAnnotationDirectly({
        ...rectangle,
        geometry: modifiedGeometry ?? [],
      }),
    );
  };

  const handleDragEndVertex = (index: number, latlng: LatLng) => {
    handleTransformEnd(getRectangleGeometryDraggedByVertex(index, latlng));
  };

  const handleRotateEnd = (latlng: LatLng) => {
    handleTransformEnd(getRectangleGeometryRotated(latlng));
  };

  const isTransforming = () => draggingVertexIndex >= 0 || isRotating;

  const showVertexMarker = (index: number) => {
    if (draggingVertexIndex < 0) {
      return true;
    }
    if (draggingVertexIndex === index) {
      return true;
    }
    return false;
  };

  return (
    <>
      {!isTransforming() && (
        <Polygon
          key={`${rectangle.id}-${draggable}-rectangle-annotation`}
          positions={rectangle.geometry.map((pt: any) => [pt.lat, pt.lng])}
          draggable={draggable}
          pathOptions={{
            ...rectangle.styles,
            color:
              highlighted || isRingfenced
                ? BLUE_HIGHLIGHT
                : rectangle.styles.color || DEFAULT_ANNOTATION_COLOR,
            weight: ShapeLineWidth,
            fill: highlighted || arrowSnappingActive,
            fillOpacity: 0.1,
            opacity: isRingfenced ? 0.5 : 1,
          }}
          eventHandlers={eventHandlers}
        />
      )}
      {isTransforming() && (
        <Polygon
          key={`${rectangle.id}-${draggable}-rectangle-dragging`}
          positions={draggingGeometry}
          draggable={false}
          pathOptions={{
            ...rectangle.styles,
            color: BLUE_HIGHLIGHT,
            weight: ShapeLineWidth,
            fill: true,
            fillOpacity: 0.1,
            opacity: 0.5,
          }}
          eventHandlers={eventHandlers}
        />
      )}
      {!isSelected && (
        <Polygon
          key={`${rectangle.id}-${draggable}-rectangle-selector`}
          positions={rectangle.geometry.map((pt: any) => [pt.lat, pt.lng])}
          draggable={false}
          pathOptions={{
            ...rectangle.styles,
            color: "black",
            weight: ShapeLineWidth + 2 * easyClickTolerance,
            fill: false,
            fillOpacity: 0,
            opacity: 0,
          }}
          eventHandlers={eventHandlers}
        />
      )}
      {isSelected &&
        draggable &&
        !isDragging &&
        !isRotating &&
        rectangle.geometry.map(
          (pos: LatLng, index: number) =>
            showVertexMarker(index) && (
              <Marker
                zIndexOffset={ZIndex.shapeGrabHandles}
                key={`rectangle-node-${rectangle.id}-${index}`}
                position={pos}
                draggable={true}
                eventHandlers={{
                  dragstart: (e) => {
                    // Prevent the marker from capturing pointer events during drag
                    e.target.getElement().style.pointerEvents = "none";
                  },
                  drag: (e) => {
                    setDraggingVertexIndex(index);
                    setDraggingGeometry(
                      getRectangleGeometryDraggedByVertex(index, e.target.getLatLng()),
                    );
                  },
                  dragend: (e) => {
                    e.target.getElement().style.pointerEvents = "";
                    setDraggingVertexIndex(-1);
                    setDraggingGeometry([]);
                    handleDragEndVertex(index, e.target.getLatLng());
                  },
                }}
                icon={circleGrabHandleIcon}
              />
            ),
        )}
      {isSelected && draggable && !isDragging && draggingVertexIndex < 0 && (
        <>
          <Polyline
            positions={getRotateHandlePositions()}
            key={`rectangle-center-${rectangle.id}-line`}
            pathOptions={{
              color: BLUE_HIGHLIGHT,
              weight: 1,
              opacity: 1,
            }}
          />
          <Marker
            zIndexOffset={ZIndex.shapeGrabHandles}
            key={`rectangle-center-${rectangle.id}-handle`}
            position={getRotateHandlePositions()[1]}
            draggable={true}
            eventHandlers={{
              dragstart: (e) => {
                // Prevent the marker from capturing pointer events during drag
                e.target.getElement().style.pointerEvents = "none";
              },
              drag: (e) => {
                setIsRotating(true);
                const draggedPos = e.target.getLatLng();
                setDraggingMarkerPosition(draggedPos);
                setDraggingGeometry(getRectangleGeometryRotated(draggedPos));
              },
              dragend: (e) => {
                e.target.getElement().style.pointerEvents = "";
                setIsRotating(false);
                setDraggingGeometry([]);
                setDraggingMarkerPosition(null);
                handleRotateEnd(e.target.getLatLng());
              },
            }}
            icon={circleGrabHandleIcon}
          />
        </>
      )}
    </>
  );
};

export default RectangleAnnotation;
